import { Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import {
  CounterpartyRiskReport,
  DrawerRiskReport,
  MaturityReport,
  PaymentPlanReport,
  PortfolioReport,
  ReconciliationReport,
  type ReportProps,
} from "../../../features/instruments/reports.js";
import { InstrumentPage } from "../../../features/instruments/shared.js";

const REPORTS = [
  "portfolio",
  "maturity",
  "payment-plan",
  "counterparty-risk",
  "drawer-risk",
  "reconciliation",
] as const;
type ReportKey = (typeof REPORTS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(REPORTS).optional().catch(undefined),
  asOf: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute("/_app/cek-senet/raporlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: InstrumentReportsPage,
});

const COMPONENTS: Record<ReportKey, (props: ReportProps) => ReactElement> = {
  portfolio: PortfolioReport,
  maturity: MaturityReport,
  "payment-plan": PaymentPlanReport,
  "counterparty-risk": CounterpartyRiskReport,
  "drawer-risk": ({ companyId }) => <DrawerRiskReport companyId={companyId} />,
  reconciliation: ({ companyId }) => <ReconciliationReport companyId={companyId} />,
};

function InstrumentReportsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const tab: ReportKey = search.tab ?? "portfolio";
  return (
    <InstrumentPage title={t("menu.instrumentReports")} description={t("instruments.reports.description")}>
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              search: (previous) => ({ company: previous.company, tab: next as ReportKey }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.instrumentReports")} className="flex-wrap">
            {REPORTS.map((report) => (
              <TabsTrigger key={report} value={report}>
                {t(`instruments.reports.tabs.${report}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          {REPORTS.map((report) => {
            const Component = COMPONENTS[report];
            return (
              <TabsContent key={report} value={report}>
                {tab === report ? (
                  <Component
                    companyId={companyId}
                    asOf={search.asOf ?? null}
                    onAsOf={(asOf) =>
                      void navigate({
                        search: (previous) => ({ ...previous, asOf: asOf ?? undefined }),
                        replace: true,
                      })
                    }
                  />
                ) : null}
              </TabsContent>
            );
          })}
        </Tabs>
      )}
    </InstrumentPage>
  );
}
